/**
 * FBT Coordinator
 * Manages request-scoped communication between add-to-cart actions and FBT decisions
 * Ensures each cart action waits for its specific FBT verdict without race conditions
 */

class FBTCoordinator {
  constructor() {
    this.pendingRequests = new Map();
    this.setupListeners();
  }

  setupListeners() {
    // Listen for FBT decision events
    document.addEventListener('fbt:will-show', (e) => this.handleDecision(e.detail?.requestId, true));
    document.addEventListener('fbt:will-not-show', (e) => this.handleDecision(e.detail?.requestId, false));
  }

  /**
   * Wait for FBT to make a decision about showing for this specific request
   * @param {string} requestId - Unique identifier for this add-to-cart action
   * @param {number} timeoutMs - Max time to wait (default: 5000ms)
   * @returns {Promise<boolean>} - true if FBT will show, false if FBT won't show or timeout
   */
  awaitDecision(requestId, timeoutMs = 5000) {
    // Guard against invalid request IDs
    if (!requestId) {
      console.warn('FBTCoordinator: Invalid requestId, resolving to false');
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      // Set up timeout fallback
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        resolve(false); // Default to opening cart drawer on timeout
      }, timeoutMs);

      // Store the resolver
      this.pendingRequests.set(requestId, {
        resolve,
        timeout
      });
    });
  }

  /**
   * Handle FBT decision event
   * @param {string} requestId - Request ID from the FBT decision
   * @param {boolean} willShow - Whether FBT will show its modal
   */
  handleDecision(requestId, willShow) {
    // Ignore decisions without valid requestId (e.g., from main product form)
    if (!requestId) {
      return;
    }

    const pending = this.pendingRequests.get(requestId);
    if (pending) {
      clearTimeout(pending.timeout);
      this.pendingRequests.delete(requestId);
      pending.resolve(willShow);
    }
  }

  /**
   * Generate a unique request ID
   * @returns {string}
   */
  static generateRequestId() {
    return `fbt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Initialize global coordinator
window.labratgiftsFBTCoordinator = new FBTCoordinator();
